import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App.jsx";
import { ToastProvider } from "./components/Toast.jsx";
import { PrefsProvider } from "./context/PrefsContext.jsx";
import { initSentry, ErrorBoundary } from "./lib/sentry.js";
import "./index.css";

// Init Sentry lo antes posible — captura errores en el propio boot.
initSentry();

/* ─── Auto-recovery de chunks staled ─────────────────────────────────────
 * Cuando deployamos nuevo build, los chunks viejos (Dashboard-XXX.js) desaparecen.
 * Si el navegador tiene cacheado el manifest viejo y trata de cargar un chunk
 * que ya no existe, falla con "Failed to fetch dynamically imported module".
 * Detectamos esto y forzamos un reload limpio (1 vez por sesión).
 */
const RELOAD_FLAG = "__portal_chunk_reload";
function isChunkLoadError(err) {
  const m = String(err?.message || err || "");
  return m.includes("Failed to fetch dynamically imported module")
      || m.includes("Importing a module script failed")
      || m.includes("error loading dynamically imported module")
      || /Loading chunk \d+ failed/.test(m);
}
function handleChunkError(err) {
  if (!isChunkLoadError(err)) return false;
  if (sessionStorage.getItem(RELOAD_FLAG)) return false; // ya reloadeamos esta sesión
  sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  console.warn("[portal/chunk-reload] stale build — recargando…");
  // Forzar bypass de service worker cache si existe
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations()
      .then(regs => Promise.all(regs.map(r => r.unregister())))
      .finally(() => window.location.reload());
  } else {
    window.location.reload();
  }
  return true;
}
window.addEventListener("error", e => handleChunkError(e.error || e.message));
window.addEventListener("unhandledrejection", e => handleChunkError(e.reason));

/* ─── PWA · registrar Service Worker del Portal ────────────────────────────
 * Detecta nuevas versiones y muestra SwUpdateBanner en vez de actualizar
 * silenciosamente (que puede dejar chunks stale en cache).
 */
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  import("virtual:pwa-register").then(({ registerSW }) => {
    const updateSW = registerSW({
      onNeedRefresh() {
        window.__swUpdate = updateSW;
        window.dispatchEvent(new CustomEvent("sw-update-available"));
      },
      onOfflineReady() {
        console.info("[PWA Portal] listo para uso offline");
      },
      immediate: true,
    });
  }).catch(err => console.warn("[PWA Portal] registro falló:", err.message));
}

// Sin StrictMode para evitar doble-mount de efectos sensibles (verify magic link)
ReactDOM.createRoot(document.getElementById("root")).render(
  <ErrorBoundary
    fallback={({ error, resetError }) => (
      <div style={{ padding: 24, fontFamily: "Figtree, system-ui, sans-serif", color: "#111318", maxWidth: 640, margin: "40px auto" }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.02em", marginBottom: 8 }}>Algo salió mal.</h1>
        <p style={{ color: "#64748b", marginBottom: 16 }}>
          Ya reportamos el error a nuestro equipo. Podés intentar recargar o volver al inicio.
        </p>
        <details style={{ marginBottom: 16, fontSize: 12, color: "#64748b" }}>
          <summary>Detalles técnicos</summary>
          <pre style={{ overflow: "auto", padding: 8, background: "#F4F3EF", borderRadius: 12, marginTop: 8 }}>
            {String(error?.message || error)}
          </pre>
        </details>
        <button
          onClick={() => { resetError(); window.location.reload(); }}
          style={{ padding: "12px 24px", background: "#111318", color: "white", border: "none", borderRadius: 999, fontWeight: 800, cursor: "pointer" }}
        >
          Recargar
        </button>
      </div>
    )}
  >
    <BrowserRouter>
      <PrefsProvider>
        <ToastProvider>
          <App />
        </ToastProvider>
      </PrefsProvider>
    </BrowserRouter>
  </ErrorBoundary>
);
